import { HTMLTable, InputGroup, NonIdealState } from "@blueprintjs/core"
import { IconNames } from "@blueprintjs/icons"
import { format, formatDistanceToNow } from "date-fns"
import { useMemo, useState } from "react"

import { PublishedModel } from "../../../api/viewModels"
import { ClickableRow, ModelsHTMLTable, MutedCell, SearchRow, TableCard } from "./Models.styled"
import { modelTicker, shortModelName } from "./shortModelName"

interface IModelsTableProps {
  models: PublishedModel[]
}

/** "4 days ago" — how long ago tells a PM what they want to know; the exact
 *  date stays on hover. */
const relativeBuiltAt = (builtAt: string | null) =>
  builtAt ? formatDistanceToNow(new Date(builtAt), { addSuffix: true }) : null

const exactBuiltAt = (builtAt: string | null) =>
  builtAt ? format(new Date(builtAt), "dd MMM yyyy HH:mm") : undefined

export const ModelsTable = ({ models }: IModelsTableProps) => {
  const [search, setSearch] = useState("")

  const visibleModels = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return models
    // The full name is searched, not the truncated label, so a version or a
    // changelog phrase finds its model. instrument is searched whole, yield
    // key included, even though the column shows it trimmed.
    return models.filter(model =>
      [model.id, model.name, model.instrument, model.currency].some(field =>
        (field ?? "").toLowerCase().includes(needle),
      ),
    )
  }, [models, search])

  if (models.length === 0) {
    return (
      <NonIdealState
        icon={IconNames.TH}
        title="No models published yet"
        description="Models appear here once they are published to view.scient.io."
      />
    )
  }

  return (
    <>
      <SearchRow>
        <InputGroup
          fill
          leftIcon={IconNames.SEARCH}
          placeholder="Search models"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </SearchRow>
      {visibleModels.length === 0 ? (
        <NonIdealState
          icon={IconNames.SEARCH}
          title="No model matches that search"
          description={`Nothing in the ${models.length} published models matches "${search}".`}
        />
      ) : (
        <TableCard>
          <ModelsHTMLTable>
            <HTMLTable striped interactive>
              <thead>
                <tr>
                  <th scope="col" className="model">
                    Model
                  </th>
                  <th scope="col" className="ticker">
                    Ticker
                  </th>
                  <th scope="col" className="currency">
                    Currency
                  </th>
                  <th scope="col" className="built">
                    Last built
                  </th>
                  <th scope="col" className="actions" />
                </tr>
              </thead>
              <tbody>
                {visibleModels.map(model => {
                  const label = shortModelName(model.name, model.id)
                  const ticker = modelTicker(model.instrument, model.name)
                  return (
                    <ClickableRow
                      key={model.id}
                      onClick={() => window.open(model.page_url, "_blank", "noreferrer")}
                    >
                      <td title={model.name ?? model.id}>{label}</td>
                      <td>{ticker ?? <MutedCell>-</MutedCell>}</td>
                      <td>{model.currency ?? <MutedCell>-</MutedCell>}</td>
                      <td title={exactBuiltAt(model.built_at)}>
                        {relativeBuiltAt(model.built_at) ?? <MutedCell>Unknown</MutedCell>}
                      </td>
                      <td>
                        <a
                          href={model.page_url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`Open ${label}`}
                          onClick={event => event.stopPropagation()}
                        >
                          Open
                        </a>
                      </td>
                    </ClickableRow>
                  )
                })}
              </tbody>
            </HTMLTable>
          </ModelsHTMLTable>
        </TableCard>
      )}
    </>
  )
}
